import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Layout from "../components/Layout";
import KPI from "../components/KPI";
import EstadoBadge from "../components/EstadoBadge";
import MapaTerritorial from "../components/MapaTerritorial";
import { useAuth } from "../lib/auth";
import { ArrowUpRight, AlertTriangle, Ghost } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/dashboard/kpis")
      .then((r) => setKpis(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !kpis) {
    return (
      <Layout>
        <div className="p-16 text-zinc-500">{t("dash_x.loading")}</div>
      </Layout>
    );
  }

  const isFacultad = user?.role === "facultad";
  const isDirectivo = user?.role === "directivo";
  const title = isFacultad
    ? t("dash_x.title_facultad")
    : isDirectivo
    ? t("dash_x.title_directivo")
    : t("dash_x.title_general");

  // Semáforo dinámico para tasa de activación
  const tasa = kpis.tasa_activacion;
  const tasaVariant = tasa >= 60 ? "activo" : tasa >= 30 ? "por_vencer" : "vencido";
  const tasaColorMap = {
    activo: { text: "text-emerald-700", bar: "bg-emerald-700", border: "border-emerald-700" },
    por_vencer: { text: "text-amber-600", bar: "bg-amber-500", border: "border-amber-500" },
    vencido: { text: "text-red-700", bar: "bg-red-700", border: "border-red-700" },
  };
  const tasaColor = tasaColorMap[tasaVariant];

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12">
        {/* Header */}
        <div className="mb-8 sm:mb-12">
          <div className="section-marker">{t("dashboard.kicker")} {isFacultad && "· " + user?.facultad_nombre}</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter mt-2">
            {title.split(".")[0]}<span className="serif-em text-zinc-900">.</span>
          </h1>
          <p className="mt-4 text-zinc-600 text-base sm:text-lg max-w-2xl">{t("dash_x.desc")}</p>
        </div>

        {/* Top KPIs row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 border-y border-zinc-200 py-8 sm:py-12 mb-8 sm:mb-12">
          <KPI label={t("dash_x.kpi_total")} value={kpis.total_convenios} testid="kpi-total" />
          <KPI label={t("dash_x.kpi_activos")} value={kpis.activos} variant="activo" testid="kpi-activos" />
          <KPI label={t("dash_x.kpi_por_vencer")} value={kpis.por_vencer} variant="por_vencer" testid="kpi-por-vencer" />
          <KPI label={t("dash_x.kpi_vencidos")} value={kpis.vencidos} variant="vencido" testid="kpi-vencidos" />
        </div>

        {/* Tasa de Activación - STAR METRIC */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
          <div className={`lg:col-span-2 border-2 ${tasaColor.border} p-6 sm:p-10 bg-white`}>
            <div className="section-marker">{t("dash_x.star")}</div>
            <h2 className="text-2xl font-medium tracking-tight mt-3">
              {t("dashboard.tasa_a")} <span className="serif-em">{t("dashboard.tasa_em")}</span>
            </h2>
            <p className="text-sm text-zinc-500 mt-2 max-w-md">{t("dashboard.tasa_desc")}</p>
            <div className="flex items-end gap-4 mt-8 flex-wrap">
              <div
                className={`kpi-number text-[clamp(4rem,14vw,12rem)] ${tasaColor.text} transition-colors`}
                data-testid="kpi-tasa-activacion"
              >
                {kpis.tasa_activacion}
                <span className="text-zinc-400">%</span>
              </div>
              <div className="pb-4 sm:pb-8 text-sm text-zinc-500">
                {kpis.convenios_con_actividad_12m} {t("dashboard.de")} {kpis.activos} {t("dashboard.activos_label")}
              </div>
            </div>
            <div className="mt-6 h-2 bg-zinc-100 w-full overflow-hidden">
              <div
                className={`h-full ${tasaColor.bar} transition-all`}
                style={{ width: `${Math.min(100, kpis.tasa_activacion)}%` }}
              ></div>
            </div>
          </div>

          <div className="border border-zinc-200 p-6 sm:p-10">
            <div className="section-marker">{t("dashboard.actividad")}</div>
            <div className="kpi-number text-5xl sm:text-6xl mt-3">{kpis.acciones_ultimos_12m}</div>
            <div className="text-sm text-zinc-500 mt-1">{t("dashboard.acciones_12m")}</div>

            <div className="mt-6 sm:mt-8 pt-6 border-t border-zinc-200">
              <div className="section-marker">{t("dashboard.cobertura")}</div>
              <div className="kpi-number text-5xl sm:text-6xl mt-3">{kpis.paises_distintos}</div>
              <div className="text-sm text-zinc-500 mt-1">{t("dashboard.paises_distintos")}</div>
            </div>
          </div>
        </div>

        {/* Mapa */}
        {!isFacultad && (
          <div className="mb-12 sm:mb-16">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-6">
              <div>
                <div className="section-marker">{t("dash_x.mapa_kicker")}</div>
                <h2 className="text-2xl sm:text-3xl font-medium tracking-tight mt-2">{t("dash_x.mapa_h")}</h2>
              </div>
              <div className="text-sm text-zinc-500">
                {t("dash_x.mapa_summary", { paises: kpis.paises_distintos, total: kpis.total_convenios })}
              </div>
            </div>
            <MapaTerritorial distribucion={kpis.distribucion_pais} />
          </div>
        )}

        {/* Ranking y Zombies */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-12 sm:mb-16">
          {/* Ranking facultades */}
          <div>
            <div className="section-marker mb-2">{t("dash_x.ranking_kicker")}</div>
            <h2 className="text-2xl sm:text-3xl font-medium tracking-tight mb-6">
              {t("dashboard.facultades_por")} <span className="serif-em">{t("dashboard.actividad_em")}</span>
            </h2>
            <div className="border-t-2 border-zinc-950 overflow-x-auto">
              <table className="w-full min-w-[480px]" data-testid="ranking-facultades">
                <thead>
                  <tr className="text-left">
                    <th className="py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("dash_x.facultad")}</th>
                    <th className="py-3 text-xs font-bold uppercase tracking-wider text-zinc-500 text-right">{t("dash_x.convenios")}</th>
                    <th className="py-3 text-xs font-bold uppercase tracking-wider text-zinc-500 text-right">{t("dash_x.acciones_12m")}</th>
                  </tr>
                </thead>
                <tbody>
                  {kpis.ranking_facultades.length === 0 && (
                    <tr><td colSpan={3} className="py-6 text-zinc-500 text-sm">{t("dash_x.no_data")}</td></tr>
                  )}
                  {kpis.ranking_facultades.map((f, i) => (
                    <tr key={f.facultad_id} className="border-t border-zinc-100 row-hover">
                      <td className="py-4">
                        <div className="flex items-center gap-3">
                          <span className="section-marker w-6">{String(i + 1).padStart(2, "0")}</span>
                          <span className="font-medium">{f.nombre}</span>
                        </div>
                      </td>
                      <td className="py-4 text-right tabular-nums">{f.total}</td>
                      <td className="py-4 text-right tabular-nums font-medium">{f.acciones}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Convenios zombies */}
          <div>
            <div className="section-marker mb-2 flex items-center gap-2">
              <Ghost size={14} strokeWidth={1.5} /> {t("dash_x.zombies_kicker")}
            </div>
            <h2 className="text-2xl sm:text-3xl font-medium tracking-tight mb-6">
              {t("dash_x.zombies_h")} <span className="serif-em">{t("dash_x.zombies_em")}</span>
            </h2>
            <div className="border border-zinc-200 p-6 bg-zinc-50 mb-4">
              <div className="text-sm ai-diamond font-medium">
                <strong>{t("dash_x.zombies_ia")}:</strong> {t("dash_x.zombies_ia_text", { n: kpis.convenios_zombies.length })}
              </div>
            </div>
            <div className="divide-y divide-zinc-100 border border-zinc-200">
              {kpis.convenios_zombies.length === 0 && (
                <div className="p-6 text-sm text-zinc-500">{t("dash_x.zombies_empty")}</div>
              )}
              {kpis.convenios_zombies.slice(0, 8).map((c) => (
                <Link
                  to={`/convenios/${c.id}`}
                  key={c.id}
                  className="flex items-start justify-between p-4 hover:bg-zinc-50 transition-colors"
                  data-testid={`zombie-${c.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium truncate">{c.institucion_nombre}</div>
                    <div className="text-xs text-zinc-500 mt-1">
                      {c.institucion_pais} · {(c.facultad_nombres || []).join(", ") || "—"}
                    </div>
                  </div>
                  <ArrowUpRight size={16} strokeWidth={1.5} className="text-zinc-400 shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Por vencer */}
        <div>
          <div className="section-marker mb-2 flex items-center gap-2">
            <AlertTriangle size={14} strokeWidth={1.5} /> {t("dash_x.alertas_kicker")}
          </div>
          <h2 className="text-2xl sm:text-3xl font-medium tracking-tight mb-6">
            {t("dash_x.alertas_h")} <span className="serif-em">{t("dash_x.alertas_em")}</span>
          </h2>
          <div className="border-t-2 border-zinc-950 overflow-x-auto">
            <table className="w-full min-w-[640px]" data-testid="por-vencer-table">
              <thead>
                <tr className="text-left">
                  <th className="py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("dash_x.col_institucion")}</th>
                  <th className="py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("dash_x.col_pais")}</th>
                  <th className="py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("dash_x.col_facultad")}</th>
                  <th className="py-3 text-xs font-bold uppercase tracking-wider text-zinc-500 text-right">{t("dash_x.col_vence")}</th>
                  <th className="py-3"></th>
                </tr>
              </thead>
              <tbody>
                {kpis.convenios_por_vencer_90d.length === 0 && (
                  <tr><td colSpan={5} className="py-6 text-zinc-500 text-sm">{t("dash_x.no_alerts")}</td></tr>
                )}
                {kpis.convenios_por_vencer_90d.map((c) => (
                  <tr key={c.id} className="border-t border-zinc-100 row-hover">
                    <td className="py-4 font-medium">{c.institucion_nombre}</td>
                    <td className="py-4 text-zinc-600">{c.institucion_pais}</td>
                    <td className="py-4 text-zinc-600">{(c.facultad_nombres || []).join(", ") || "—"}</td>
                    <td className="py-4 text-right tabular-nums">
                      <span className={c.dias_restantes <= 30 ? "text-amber-700 font-medium" : "text-zinc-700"}>
                        {c.dias_restantes} {t("dash_x.days")}
                      </span>
                    </td>
                    <td className="py-4 text-right">
                      <Link to={`/convenios/${c.id}`} className="text-sm underline" data-testid={`open-${c.id}`}>
                        {t("dash_x.ver")}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
